import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Truck, Star, MapPin, Loader2, ArrowLeft, ChevronRight, Send,
  Calendar, Users, CheckCircle, ListChecks, AlertCircle, DollarSign,
  Image as ImageIcon, Sparkles,
} from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbLD, foodTruckProductLD, foodTruckVendorLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';

const EVENT_TYPES = [
  'Wedding', 'Corporate event', 'Birthday party',
  'Private party', 'Festival', 'Pop-up', 'Other',
];

const capitalize = (s = '') => String(s).replace(/\b\w/g, (m) => m.toUpperCase());
const citySlugToName = (s = '') => capitalize(String(s).replace(/-/g, ' '));

export default function FoodTruckDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const r = await axios.get(`${API}/public/food-trucks/${slug}`);
      setData(r.data);
      setActiveImage(r.data?.image_url || r.data?.gallery_images?.[0] || null);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error('Could not load this food truck — try again');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-32 flex justify-center" data-testid="food-truck-detail-loading">
        <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-background pt-32 px-4" data-testid="food-truck-detail-notfound">
        <Card className="max-w-xl mx-auto border-rose-500/30 bg-rose-500/5">
          <CardContent className="p-8 text-center space-y-3">
            <AlertCircle className="h-10 w-10 text-rose-400 mx-auto" />
            <h2 className="text-2xl font-bold">Food truck not found</h2>
            <p className="text-sm text-muted-foreground">
              This listing may have moved or sold out. Browse other food trucks below.
            </p>
            <Link to="/food-trucks">
              <Button className="bg-gradient-to-r from-orange-500 to-rose-500 text-white">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to food trucks
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // -------- Render: vendor "brand" page (lists their menus) --------
  if (data.kind === 'vendor') {
    return <VendorView data={data} />;
  }

  // -------- Render: single service_product menu --------
  const cityName = citySlugToName(data.city || '');
  const galleryImages = [data.image_url, ...(data.gallery_images || [])].filter(Boolean);
  const canonicalUrl = `${SITE_URL}/food-trucks/${data.slug}`;
  const breadcrumb = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: 'Food Trucks', url: '/food-trucks' },
    ...(data.city ? [{ name: cityName, url: `/${data.city}/food-trucks` }] : []),
    { name: data.name, url: `/food-trucks/${data.slug}` },
  ]);
  const productLD = foodTruckProductLD(data, data.slug);

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="food-truck-detail">
      <SEO
        title={`${data.name} — Food Truck Catering | Eventism`}
        description={data.short_description || data.description?.slice(0, 200) || `Book ${data.name} for your event.`}
        canonical={canonicalUrl}
        url={canonicalUrl}
        image={data.image_url}
        type="product"
        structuredData={[productLD, breadcrumb].filter(Boolean)}
      />

      {/* Breadcrumb / back */}
      <section className="max-w-6xl mx-auto px-4 mb-6 flex items-center gap-2 text-xs text-muted-foreground">
        <Link to="/food-trucks" className="hover:text-orange-400 flex items-center gap-1" data-testid="bc-food-trucks">
          <ArrowLeft className="h-3 w-3" /> Food trucks
        </Link>
        {data.city && (
          <>
            <ChevronRight className="h-3 w-3" />
            <Link to={`/${data.city}/food-trucks`} className="hover:text-orange-400" data-testid="bc-city">
              {cityName}
            </Link>
          </>
        )}
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground truncate">{data.name}</span>
      </section>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-3">
          {activeImage ? (
            <div className="aspect-video rounded-lg overflow-hidden bg-card">
              <img
                src={activeImage}
                alt={data.name}
                className="w-full h-full object-cover"
                data-testid="food-truck-hero-image"
              />
            </div>
          ) : (
            <div className="aspect-video rounded-lg bg-card flex items-center justify-center">
              <ImageIcon className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
          {galleryImages.length > 1 && (
            <div className="flex gap-2 overflow-x-auto" data-testid="food-truck-gallery">
              {galleryImages.map((img) => (
                <button
                  key={img}
                  onClick={() => setActiveImage(img)}
                  className={`h-16 w-24 shrink-0 rounded-md overflow-hidden border-2 transition-colors ${
                    activeImage === img ? 'border-orange-400' : 'border-transparent hover:border-border'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-2 space-y-4">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-orange-400 flex items-center gap-2">
            <Truck className="h-3.5 w-3.5" /> {data.sub_category ? capitalize(data.sub_category.replace(/-/g, ' ')) : 'Food truck'}
          </p>
          <h1
            className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] leading-tight"
            data-testid="food-truck-name"
          >
            {data.name}
          </h1>
          {data.short_description && (
            <p className="text-base text-muted-foreground">{data.short_description}</p>
          )}
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {data.city && (
              <span className="flex items-center gap-1 text-muted-foreground" data-testid="ft-city">
                <MapPin className="h-4 w-4 text-orange-400" /> {cityName}
              </span>
            )}
            {data.vendor?.rating != null && data.vendor.review_count > 0 && (
              <span className="flex items-center gap-1 text-amber-300 font-medium">
                <Star className="h-4 w-4 fill-amber-300" />
                {Number(data.vendor.rating).toFixed(1)}
                <span className="text-muted-foreground font-normal">
                  ({data.vendor.review_count})
                </span>
              </span>
            )}
          </div>
          {data.base_price != null && (
            <div className="rounded-lg border border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-rose-500/5 p-4" data-testid="ft-pricing">
              <p className="text-[11px] uppercase tracking-wide text-orange-400 font-bold">From</p>
              <p className="text-3xl font-bold mt-1">
                ${data.base_price}
                <span className="text-sm text-muted-foreground font-normal ml-1">
                  {data.pricing_unit_label || 'per guest'}
                </span>
              </p>
              {data.min_guests && data.max_guests && (
                <p className="text-xs text-muted-foreground mt-1">
                  <Users className="h-3 w-3 inline mr-1" />
                  {data.min_guests}–{data.max_guests} guests
                </p>
              )}
            </div>
          )}
          <a href="#quote">
            <Button
              className="w-full h-12 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-bold text-base"
              data-testid="ft-cta-quote"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Get instant quote
            </Button>
          </a>
          {data.vendor && (
            <Card className="border-border">
              <CardContent className="p-4 flex items-center gap-3">
                {(data.vendor.cover_image || data.vendor.image_url) && (
                  <img
                    src={data.vendor.cover_image || data.vendor.image_url}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">By</p>
                  <p className="font-bold truncate">{data.vendor.name}</p>
                </div>
                {data.vendor.slug && (
                  <Link to={`/food-trucks/${data.vendor.slug}`}>
                    <Button variant="ghost" size="sm" className="text-xs">View all menus</Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* About */}
      {data.description && (
        <section className="max-w-6xl mx-auto px-4 mt-12 space-y-3" data-testid="ft-about">
          <h2 className="text-2xl font-bold">About this menu</h2>
          <div className="prose prose-invert max-w-none text-muted-foreground whitespace-pre-line">
            {data.description}
          </div>
        </section>
      )}

      {/* What to expect */}
      {data.what_to_expect && (
        <section className="max-w-6xl mx-auto px-4 mt-10" data-testid="ft-expect">
          <Card className="border-border">
            <CardContent className="p-5 space-y-2">
              <h3 className="font-bold flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" /> What to expect
              </h3>
              <p className="text-sm text-muted-foreground">{data.what_to_expect}</p>
            </CardContent>
          </Card>
        </section>
      )}

      {/* What's included + Host requirements */}
      <section className="max-w-6xl mx-auto px-4 mt-6 grid md:grid-cols-2 gap-4">
        {data.whats_included?.length > 0 && (
          <Card className="border-border" data-testid="ft-included">
            <CardContent className="p-5 space-y-3">
              <h3 className="font-bold flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-emerald-400" /> What's included
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {data.whats_included.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
        {data.host_requirements?.length > 0 && (
          <Card className="border-border" data-testid="ft-host-reqs">
            <CardContent className="p-5 space-y-3">
              <h3 className="font-bold flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-400" /> Host requirements
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {data.host_requirements.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <ChevronRight className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Menu options */}
      {data.option_groups?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 mt-12 space-y-4" data-testid="ft-options">
          <h2 className="text-2xl font-bold">Customize your menu</h2>
          <p className="text-sm text-muted-foreground">
            Build your service by picking from each group. We'll quote the total when you request a booking.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {data.option_groups.map((g) => (
              <Card key={g.id || g.label} className="border-border" data-testid={`ft-group-${(g.id || g.label || '').toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold">{g.label}</h3>
                    <Badge variant="outline" className="text-[10px]">
                      Pick {g.min_select}{g.max_select && g.max_select !== g.min_select ? `–${g.max_select}` : ''}
                    </Badge>
                  </div>
                  <ul className="space-y-1.5">
                    {(g.items || []).map((it) => (
                      <li key={it.name} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          {(it.default || it.is_default) && (
                            <span className="text-[9px] uppercase font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                              Default
                            </span>
                          )}
                          {it.name}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {it.price_delta === 0
                            ? 'included'
                            : it.price_delta > 0
                              ? `+$${it.price_delta}`
                              : `-$${Math.abs(it.price_delta)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Sibling menus */}
      {data.siblings?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 mt-12 space-y-4" data-testid="ft-siblings">
          <h2 className="text-2xl font-bold">Other menus from {data.vendor?.name || 'this truck'}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.siblings.map((s) => (
              <Link key={s.slug} to={s.url} data-testid={`ft-sibling-${s.slug}`} className="block group">
                <Card className="border-border hover:border-orange-500/40 transition-colors h-full overflow-hidden">
                  {s.image_url && (
                    <div className="aspect-video bg-card overflow-hidden">
                      <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                  )}
                  <CardContent className="p-3 space-y-1">
                    <p className="font-bold truncate">{s.name}</p>
                    {s.short_description && (
                      <p className="text-xs text-muted-foreground line-clamp-2">{s.short_description}</p>
                    )}
                    {s.base_price != null && (
                      <p className="text-xs font-semibold text-orange-300">From ${s.base_price}</p>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Quote form */}
      <QuoteSection
        cityHint={data.city}
        prefillNotes={`Interested in: ${data.name}. ${data.short_description || ''}`.slice(0, 300)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vendor "brand" view — when slug resolves to a vendor row, list their menus
// ---------------------------------------------------------------------------
function VendorView({ data }) {
  const canonicalUrl = `${SITE_URL}/food-trucks/${data.slug}`;
  const breadcrumb = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: 'Food Trucks', url: '/food-trucks' },
    { name: data.name, url: `/food-trucks/${data.slug}` },
  ]);
  const vendorLD = foodTruckVendorLD(data, data.slug);

  // Group menus by city
  const menusByCity = (data.menus || []).reduce((acc, m) => {
    const c = m.city || 'other';
    (acc[c] = acc[c] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="food-truck-vendor">
      <SEO
        title={`${data.name} — Food Truck Catering | Eventism`}
        description={data.description?.slice(0, 200) || `Book ${data.name} for your event.`}
        canonical={canonicalUrl}
        url={canonicalUrl}
        image={data.image_url}
        type="profile"
        structuredData={[vendorLD, breadcrumb].filter(Boolean)}
      />

      <section className="max-w-6xl mx-auto px-4 mb-6 flex items-center gap-2 text-xs text-muted-foreground">
        <Link to="/food-trucks" className="hover:text-orange-400 flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Food trucks
        </Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground truncate">{data.name}</span>
      </section>

      <section className="max-w-6xl mx-auto px-4 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          {data.image_url ? (
            <div className="aspect-video rounded-lg overflow-hidden bg-card">
              <img src={data.image_url} alt={data.name} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="aspect-video rounded-lg bg-card flex items-center justify-center">
              <Truck className="h-12 w-12 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="lg:col-span-2 space-y-3">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-orange-400 flex items-center gap-2">
            <Truck className="h-3.5 w-3.5" /> Food truck
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] leading-tight" data-testid="vendor-name">
            {data.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {data.city && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <MapPin className="h-4 w-4 text-orange-400" /> {capitalize(data.city)}
              </span>
            )}
            {data.rating != null && data.review_count > 0 && (
              <span className="flex items-center gap-1 text-amber-300 font-medium">
                <Star className="h-4 w-4 fill-amber-300" />
                {Number(data.rating).toFixed(1)}
                <span className="text-muted-foreground font-normal">({data.review_count})</span>
              </span>
            )}
            {data.price_from != null && (
              <span className="flex items-center gap-1 text-orange-300 font-semibold">
                <DollarSign className="h-4 w-4" /> From ${data.price_from}
              </span>
            )}
          </div>
          {data.description && (
            <p className="text-sm text-muted-foreground whitespace-pre-line">{data.description}</p>
          )}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 mt-12 space-y-6" data-testid="vendor-menus">
        <h2 className="text-2xl font-bold">All menus &amp; cities</h2>
        {Object.keys(menusByCity).sort().map((city) => (
          <div key={city} className="space-y-3">
            <h3 className="text-sm uppercase tracking-wide font-bold text-orange-400">
              {capitalize(city.replace(/-/g, ' '))}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {menusByCity[city].map((m) => (
                <Link key={m.slug} to={m.url} data-testid={`vendor-menu-${m.slug}`} className="block group">
                  <Card className="border-border hover:border-orange-500/40 transition-colors h-full overflow-hidden">
                    {m.image_url && (
                      <div className="aspect-video bg-card overflow-hidden">
                        <img src={m.image_url} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </div>
                    )}
                    <CardContent className="p-3 space-y-1">
                      <p className="font-bold text-sm truncate">{m.name}</p>
                      {m.base_price != null && (
                        <p className="text-xs font-semibold text-orange-300">From ${m.base_price}</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        ))}
        {(!data.menus || data.menus.length === 0) && (
          <Card className="border-dashed border-2 border-border">
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No active menus yet — check back soon.
            </CardContent>
          </Card>
        )}
      </section>

      <QuoteSection cityHint={data.city} prefillNotes={`Interested in: ${data.name}`} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quote form — reused pattern from FoodTrucksLanding
// ---------------------------------------------------------------------------
function QuoteSection({ cityHint, prefillNotes }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    event_type: 'Corporate event',
    event_date: '', guest_count: 100,
    notes: prefillNotes || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/leads`, {
        client_name: form.name,
        client_email: form.email,
        client_phone: form.phone,
        event_type: form.event_type.toLowerCase().replace(/\s+/g, '_'),
        event_date: form.event_date || null,
        guest_count: Number(form.guest_count) || 0,
        notes: form.notes,
        source: cityHint ? `food-truck-detail-${cityHint}` : 'food-truck-detail',
        city: cityHint || '',
      });
      setSent(true);
      toast.success('Quote request sent — we\'ll reach out within 24 hours.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <section id="quote" className="max-w-3xl mx-auto px-4 mt-16" data-testid="ft-quote-sent">
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="p-8 text-center space-y-3">
            <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto" />
            <h3 className="text-2xl font-bold">Quote request received</h3>
            <p className="text-muted-foreground">
              We'll email a curated proposal within 24 hours to <strong>{form.email}</strong>.
            </p>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section id="quote" className="max-w-3xl mx-auto px-4 mt-16" data-testid="ft-quote">
      <Card className="border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-rose-500/5">
        <CardContent className="p-6 sm:p-8 space-y-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-orange-400">
              Instant quote
            </p>
            <h2 className="text-3xl font-bold mt-1">Ready to book this truck?</h2>
            <p className="text-sm text-muted-foreground mt-1">
              We'll confirm availability and lock in your date — usually within 24 hours.
            </p>
          </div>

          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
            <Field label="Your name *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="ftd-quote-name" />
            </Field>
            <Field label="Email *">
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="ftd-quote-email" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ftd-quote-phone" />
            </Field>
            <Field label="Event type">
              <select
                value={form.event_type}
                onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm"
                data-testid="ftd-quote-event-type"
              >
                {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Event date">
              <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="ftd-quote-date" />
            </Field>
            <Field label="Guest count">
              <Input type="number" min="1" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} data-testid="ftd-quote-guests" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Anything else? (cuisine preferences, venue notes)">
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="ftd-quote-notes"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-bold"
                data-testid="ftd-quote-submit-btn"
              >
                {submitting
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                  : <><Send className="h-4 w-4 mr-2" /> Get my quote</>}
              </Button>
            </div>
          </form>
          <div className="flex items-center gap-4 text-[11px] text-muted-foreground pt-2 border-t border-border">
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Quote in 24h</span>
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> 10–10k guests</span>
            <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Licensed + insured</span>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase font-bold text-muted-foreground tracking-wide">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
